import { useEffect, useState, FormEvent } from "react";
import { Loader2, Pencil, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { invalidateContent, mediaUrl } from "@/lib/content";
import { SERVICES } from "@/data/site";
import { SUBURBS, uploadPhoto } from "./GalleryManager";

type CS = { id?: string; title: string; location: string; service: string; area: string; duration: string; challenge: string; solution: string; result: string; image_id: string };
const EMPTY: CS = { title: "", location: "", service: "", area: "", duration: "", challenge: "", solution: "", result: "", image_id: "" };

export default function CaseStudyManager() {
  const [items, setItems] = useState<CS[]>([]);
  const [f, setF] = useState<CS>(EMPTY);
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const set = (k: keyof CS) => (e: { target: { value: string } }) => setF({ ...f, [k]: e.target.value });

  useEffect(() => { api.get("/case-studies").then((r) => setItems(r.data)); }, []);

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setBusy(true);
    try {
      let image_id = f.image_id;
      if (file) image_id = (await uploadPhoto(file, { kind: "case_study", title: f.title })).id;
      const { id, ...body } = { ...f, image_id };
      const { data } = id ? await api.put(`/case-studies/${id}`, body) : await api.post("/case-studies", body);
      setItems(id ? items.map((x) => (x.id === id ? data : x)) : [data, ...items]);
      setF(EMPTY);
      setFile(null);
      invalidateContent();
      toast.success(id ? "Case study updated" : "Case study published");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setBusy(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm("Delete this case study from the website?")) return;
    await api.delete(`/case-studies/${id}`);
    setItems(items.filter((x) => x.id !== id));
    invalidateContent();
  };

  const field = (k: keyof CS, ph: string, testid: string) => <input className="crm-field" placeholder={ph} value={f[k] as string} onChange={set(k)} data-testid={testid} />;

  return (
    <section className="border border-slate-800 bg-[#161920] p-5" data-testid="case-study-manager">
      <h2 className="mb-4 font-display text-2xl font-bold uppercase text-white">Case studies</h2>
      <form onSubmit={submit} className="grid gap-2 md:grid-cols-3" data-testid="case-study-form">
        {field("title", "Project title *", "cs-title-input")}
        <select className="crm-field" value={f.location} onChange={set("location")} data-testid="cs-location-select">
          <option value="">Suburb</option>{SUBURBS.map((s) => <option key={s}>{s}</option>)}
        </select>
        <select className="crm-field" value={f.service} onChange={set("service")} data-testid="cs-service-select">
          <option value="">Service</option>{SERVICES.map((s) => <option key={s.slug}>{s.title}</option>)}
        </select>
        {field("area", "Floor area (e.g. 1,200 m²)", "cs-area-input")}
        {field("duration", "Time on site (e.g. 5 days)", "cs-duration-input")}
        <input type="file" accept=".jpg,.jpeg,.png,.webp" onChange={(e) => setFile(e.target.files?.[0] || null)} className="crm-field" data-testid="cs-image-input" />
        <textarea className="crm-field md:col-span-3" rows={2} placeholder="Challenge" value={f.challenge} onChange={set("challenge")} data-testid="cs-challenge-input" />
        <textarea className="crm-field md:col-span-3" rows={2} placeholder="Solution" value={f.solution} onChange={set("solution")} data-testid="cs-solution-input" />
        <textarea className="crm-field md:col-span-3" rows={2} placeholder="Result" value={f.result} onChange={set("result")} data-testid="cs-result-input" />
        <div className="flex gap-2 md:col-span-3">
          <button disabled={busy} className="btn-brand !min-h-0 flex-1 !py-2 !text-base" data-testid="cs-save-button">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} {f.id ? "Save changes" : "Publish case study"}
          </button>
          {f.id && <button type="button" onClick={() => setF(EMPTY)} className="btn-ghost !min-h-0 !py-2 !text-base" data-testid="cs-cancel-button">Cancel</button>}
        </div>
      </form>
      <ul className="mt-5 divide-y divide-slate-800">
        {items.length === 0 && <li className="text-sm text-slate-500" data-testid="cs-empty">No case studies yet — the website shows sample case studies until you publish your own.</li>}
        {items.map((c) => (
          <li key={c.id} className="flex items-center gap-3 py-3" data-testid={`cs-item-${c.id}`}>
            {c.image_id ? <img src={mediaUrl(c.image_id)} alt="" className="h-12 w-16 object-cover" /> : <div className="h-12 w-16 bg-slate-800" />}
            <div className="min-w-0 flex-1">
              <div className="truncate text-white">{c.title}</div>
              <div className="truncate text-xs text-slate-500">{[c.location, c.service, c.area].filter(Boolean).join(" · ")}</div>
            </div>
            <button onClick={() => { setF({ ...EMPTY, ...c }); window.scrollTo({ top: 0, behavior: "smooth" }); }} aria-label="Edit" data-testid={`cs-edit-${c.id}`} className="p-2 text-slate-400 hover:text-brand-400"><Pencil className="h-4 w-4" /></button>
            <button onClick={() => remove(c.id!)} aria-label="Delete" data-testid={`cs-delete-${c.id}`} className="p-2 text-slate-400 hover:text-red-400"><Trash2 className="h-4 w-4" /></button>
          </li>
        ))}
      </ul>
    </section>
  );
}
